// Lens layer: paints the per-hex lens value (hexdata state.a) over the land in the legend colours.
//
//   state.a byte ──► lens table (256 × RGBA, lensLut.ts) ──► legend colour
//   legend colour ──► albedo the lighting turns back into that colour ──► terrain relief ──► outline
//
//   colour ........ value drawn in the legend colour, so map, minimap and legend agree; category
//                   lenses fill whole hexes, scalar lenses blend the byte between hex centres
//   no data ....... hatched grey (NaN, and hexes past the legend): never a colour implying a value
//   sea ........... untouched here; the sea veil dims it under a lens (seaVeil.ts)
//
// One uniform, `lensAmount`, runs 0 → 1 over LENS_FADE_S when a lens is set and back when it is
// cleared, so the terrain desaturates and the colours arrive together. The shader is compiled in
// once and gated by that uniform: switching lenses changes a texture, never a pipeline.

import { If, float, int, ivec2, max, mix, pow, select, smoothstep, step, textureLoad, uniform, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { TERRAIN } from '../../core/schema.ts';
import type { LensResult } from '../../world/api.ts';
import type { SurfaceCtx, SurfaceLayer } from '../surface.ts';
import { rgb, screenNode } from '../tsl/color.ts';
import { INRADIUS, cellCoord, hexBary } from '../tsl/hex.ts';
import { buildLensLut, lensBytes } from './lensLut.ts';
import { CHANNEL, LUT_ROW, type OverlayData } from './overlayData.ts';
import { luma, stripes } from './patterns.ts';
import { approach, ease } from './tween.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** Layer order: after lakes and rivers, before borders (300) so borders stay on top. */
export const LENS_ORDER = 250;
const LENS_FADE_S = 0.25;
const AMOUNT_EPSILON = 0.001;
const BYTE = 255;
const NO_DATA_BYTE = 255;

// The land keeps only its light and dark: the legend colour times a mild terrain shade, so a hex
// reads as its swatch (the map is graded warm, so the albedo is corrected for it).
const RELIEF_KEEP = 0.2;
const RELIEF_MID = 0.22;
const RELIEF_RANGE = [0.7, 1.3] as const;
/** How far the terrain is pulled toward grey where there is no data. */
const DESATURATE = 0.78;

/**
 * What the light rig does to flat ground per linear channel (lighting.ts: warm key, cool fill),
 * measured from renders of flat swatches: the pixel is albedo × this. screen() only knows the
 * average (FLAT_LIGHT), so a legend colour would drift warm without it.
 */
const FLAT_TINT = [1, 0.92, 0.79] as const;

// Khronos Neutral tone map: peaks above the knee are compressed, so a bright swatch needs more
// albedo than screen() gives it. The target peak is capped so a white swatch stays finite.
const NEUTRAL_START = 0.76;
const PEAK_CAP = 0.94;

// No data: mid-grey paper with darker diagonal hatching.
const NO_DATA_GREY = '#9a9a94';
const NO_DATA_INK = '#5c5c58';
const HATCH_FREQ = 4.2;
const HATCH_DUTY = 0.3;
const HATCH_STRENGTH = 0.85;

// Hex outline: a thin dark line that fades once a hex spans few pixels.
const OUTLINE_PX = 1.1;
const OUTLINE_DARKEN = 0.24;
const OUTLINE_HEX_PX = [8, 22] as const;

// sRGB transfer function (IEC 61966-2-1).
const SRGB_KNEE = 0.04045;
const SRGB_LINEAR_SLOPE = 12.92;
const SRGB_OFFSET = 0.055;
const SRGB_GAMMA = 2.4;

/** sRGB (0..1) to linear light. */
function srgbToLinear(c: V3): V3 {
  const curve = pow(c.add(SRGB_OFFSET).div(1 + SRGB_OFFSET), vec3(SRGB_GAMMA));
  const low = c.div(SRGB_LINEAR_SLOPE);
  return low.add(curve.sub(low).mul(step(vec3(SRGB_KNEE), c)));
}

/** Inverse of the Neutral highlight compression: the peak the light must deliver to land on `c`. */
function preCompress(c: V3): V3 {
  const peak = max(c.x, max(c.y, c.z)).clamp(1e-4, PEAK_CAP);
  const span = 1 - NEUTRAL_START;
  const need = select(peak.greaterThan(NEUTRAL_START), float(span * span).div(float(1).sub(peak)).sub(span).add(NEUTRAL_START), peak);
  return c.mul(need.div(peak));
}

export interface LensLayer {
  layer: SurfaceLayer;
  /** 0..1, eased: how far the lens has faded in. */
  amount: Node<'float'>;
  /** Value of `amount` right now (for other layers that follow the lens on the CPU). */
  current(): number;
  set(lens: LensResult | null): void;
  update(dt: number): void;
  dispose(): void;
}

export function createLens(data: OverlayData): LensLayer {
  const amountU = uniform(0);
  /** 1 for a scalar lens (bytes blend between hex centres), 0 for a category lens. */
  const blendU = uniform(0);
  let target = 0;
  let raw = 0;

  /** Lens byte of the fragment: its own hex, or the eased blend of the three nearest centres. */
  function lensByte(ctx: SurfaceCtx): F {
    const own = textureLoad(data.texture, ctx.hex.coord).x.mul(BYTE).round();
    const { cells, w } = hexBary(ctx.hex);
    let sum: F = float(0);
    let share: F = float(0);
    cells.forEach((cell, i) => {
      const b = textureLoad(data.texture, cellCoord(cell)).x.mul(BYTE).round();
      const weight = [w.x, w.y, w.z][i].mul(select(b.lessThan(NO_DATA_BYTE), 1, 0));
      sum = sum.add(weight.mul(b));
      share = share.add(weight);
    });
    const blended = sum.div(share.max(1e-5)).round();
    return select(own.lessThan(NO_DATA_BYTE), mix(own, blended, blendU), own);
  }

  /**
   * Legend colour and no-data hatch over `ctx.color`, land only. The work sits behind one branch, so
   * a map without a lens pays for a comparison. Two TSL rules shape it:
   *   - the branch fills variables that the result reads afterwards; assigning into the returned
   *     variable does not take;
   *   - a variable first read inside a branch is declared inside it, so the inputs are pinned to
   *     variables before the branch.
   */
  function paint(ctx: SurfaceCtx): V3 {
    const painted = vec3(0).toVar('lensPainted');
    const gate = float(0).toVar('lensGate');
    const under = ctx.color.toVar('lensUnder');
    const lum = luma(under).toVar('lensLuma');
    const active = ctx.data.terrainAt(ctx.hex).terrain.greaterThan(TERRAIN.COAST + 0.5).and(amountU.greaterThan(AMOUNT_EPSILON));

    If(active, () => {
      const byte = lensByte(ctx);
      const entry = textureLoad(data.texture, ivec2(int(byte), int(LUT_ROW)));
      const known = entry.w;

      // The legend colour as albedo, with the terrain's own light and dark on top.
      const shade = lum.div(RELIEF_MID).clamp(RELIEF_RANGE[0], RELIEF_RANGE[1]).sub(1).mul(RELIEF_KEEP).add(1);
      const muted = mix(under, vec3(lum), DESATURATE);
      const albedo = screenNode(preCompress(srgbToLinear(entry.xyz))).div(vec3(...FLAT_TINT));
      const tinted = albedo.mul(shade);

      // No data: grey paper, hatched.
      const hatch = stripes(ctx.xz, HATCH_FREQ, HATCH_DUTY, ctx.px).mul(HATCH_STRENGTH);
      const grey = mix(mix(muted, rgb(NO_DATA_GREY), 0.7), rgb(NO_DATA_INK), hatch);
      const filled = mix(grey, tinted, known);

      // Outline.
      const hexPx = float(2 * INRADIUS).div(ctx.px.max(1e-5));
      const outline = smoothstep(ctx.px.mul(OUTLINE_PX), float(0), ctx.hex.minEdge).mul(smoothstep(OUTLINE_HEX_PX[0], OUTLINE_HEX_PX[1], hexPx));

      painted.assign(filled.mul(float(1).sub(outline.mul(OUTLINE_DARKEN))));
      gate.assign(amountU);
    });

    return mix(under, painted, gate);
  }

  return {
    layer: { name: 'lens', order: LENS_ORDER, color: paint },
    amount: amountU as unknown as F,
    current: () => amountU.value,
    set(lens) {
      target = lens ? 1 : 0;
      if (!lens) {
        return;
      }
      blendU.value = lens.kind === 'scalar' ? 1 : 0;
      data.writeTable(buildLensLut(lens));
      data.write(CHANNEL.LENS, lensBytes(lens));
    },
    update(dt) {
      if (raw === target) {
        return;
      }
      raw = approach(raw, target, dt, LENS_FADE_S);
      amountU.value = ease(raw);
    },
    dispose() {},
  };
}
